
/* ONDO ring gift. One instance, locale-aware cart reads, existing ONDO cart writer.
 * Product options: Size (index 0), Color (index 1), as in the supplied Donut Ring.
 * Discount is always calculated by Shopify, never by this script.
 */
(() => {
  if (window.OndoRingGift) { window.OndoRingGift.mount(); return; }
  const MARK = '_ondo_ring_gift';
  let locked = false;
  let cleanup = false;
  let pending = null;
  let noticeTimer;
  const config = () => {
    const node = document.querySelector('[data-ondo-ring-gift-config]');
    try {
      if (!node) return null;
      const cfg = JSON.parse(node.textContent);
      cfg.eligible = (cfg.eligible || []).map(Number);
      cfg.giftId = Number(cfg.giftId) || 0;
      return cfg;
    } catch (_) { return null; }
  };
  const root = () => window.Shopify?.routes?.root || '/';
  const cfgError = () => config()?.error || window.cartStrings?.error || '';
  async function readCart() {
    const control = new AbortController();
    const timeout = setTimeout(() => control.abort(), 15000);
    try {
      const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: control.signal });
      if (!response.ok) throw new Error(cfgError());
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function notice(message) {
    const el = document.getElementById('ondo-ring-gift-notice');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { el.hidden = true; }, 12000);
  }
  function mount() {
    const cfg = config();
    if (!cfg) return;
    document.querySelectorAll('ondo-product-form').forEach(form => {
      const eligible = cfg.enabled && cfg.giftId && cfg.eligible.includes(Number(form.dataset.productId));
      let intro = form.querySelector('.ondo-ring-gift-intro');
      if (!eligible || !form.form) {
        if (intro) intro.remove();
        if (form.dataset.giftOldLabel) {
          form.dataset.labelAdd = form.dataset.giftOldLabel;
          delete form.dataset.giftOldLabel;
          if (form.state && !form.busy) form.setState(form.state);
        }
        return;
      }
      if (!intro) {
        intro = document.createElement('div');
        intro.className = 'ondo-ring-gift-intro';
        form.form.before(intro);
      }
      // Trusted merchant richtext, escaped as JSON by Liquid. No product/customer input here.
      if (intro.innerHTML !== cfg.intro) intro.innerHTML = cfg.intro;
    });
  }
  function choose(cfg, trigger) {
    const dialog = document.getElementById('ondo-ring-gift-dialog');
    if (!dialog || !dialog.showModal) return Promise.reject(new Error(cfg.error));
    const colour = dialog.querySelector('[data-gift-colour]');
    const size = dialog.querySelector('[data-gift-size]');
    const button = dialog.querySelector('[data-gift-confirm]');
    const photo = dialog.querySelector('[data-gift-image]');
    const error = dialog.querySelector('[data-gift-error]');
    const variants = cfg.variants || [];
    const available = variants.filter(v => v.available);
    if (!available.length) return Promise.reject(new Error(cfg.soldout));
    const option = (value, label) => { const el = document.createElement('option'); el.value = value; el.textContent = label; return el; };
    colour.replaceChildren(option('', cfg.choose), ...[...new Set(available.map(v => v.options[1]))].map(c => option(c,c)));
    size.replaceChildren(option('', cfg.choose));
    size.disabled = true;
    button.disabled = true;
    error.hidden = true;
    const updatePhoto = variant => {
      photo.hidden = !variant?.image;
      if (variant?.image) photo.src = variant.image;
    };
    updatePhoto(available[0]);
    let selected = null;
    const onColour = () => {
      const previous = size.value;
      const matches = available.filter(v => v.options[1] === colour.value);
      size.replaceChildren(option('', cfg.choose), ...[...new Set(matches.map(v => v.options[0]))].map(s => option(s,s)));
      size.disabled = !matches.length;
      if (matches.some(v => v.options[0] === previous)) size.value = previous;
      updatePhoto(matches[0]);
      onSize();
    };
    const onSize = () => {
      selected = available.find(v => v.options[0] === size.value && v.options[1] === colour.value) || null;
      button.disabled = !selected;
      if (selected) updatePhoto(selected);
    };
    return new Promise(resolve => {
      const done = value => {
        if (pending !== done) return;
        pending = null;
        colour.removeEventListener('change', onColour);
        size.removeEventListener('change', onSize);
        button.removeEventListener('click', confirm);
        dialog.removeEventListener('cancel', cancel);
        dialog.querySelector('[data-gift-close]').removeEventListener('click', cancel);
        if (dialog.open) dialog.close();
        if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
        resolve(value);
      };
      const cancel = e => { e.preventDefault(); done(null); };
      const confirm = () => { if (selected) done(selected); };
      pending = done;
      colour.addEventListener('change', onColour);
      size.addEventListener('change', onSize);
      button.addEventListener('click', confirm);
      dialog.addEventListener('cancel', cancel);
      dialog.querySelector('[data-gift-close]').addEventListener('click', cancel);
      dialog.showModal();
      colour.focus();
    });
  }
  async function rollback(tx, engine) {
    const cart = await readCart();
    const keys = cart.items.filter(i => i.properties?._ondo_ring_tx === tx).map(i => i.key);
    if (keys.length) {
      const result = await engine.removeLines(keys);
      if (!result.ok) throw new Error(cfgError());
    }
  }
  // A cart restored from a previous visit may still be initializing.
  // Wait briefly instead of rejecting a valid click immediately.
  async function waitForCart(engine, cfg) {
    const until = Date.now() + 8000;
    while (engine.isBusy()) {
      if (Date.now() >= until) throw new Error(cfg.working);
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  async function purchaseCart(engine, cfg) {
    await waitForCart(engine, cfg);
    let cart = await readCart();
    // Repair only explicitly marked gifts. Never delete an ordinary paid Donut.
    if (invalidGift(cart, cfg)) {
      const result = await engine.refresh();
      if (!result.ok) throw new Error(cfg.error);
      await waitForCart(engine, cfg);
      cart = await readCart();
      if (invalidGift(cart, cfg)) throw new Error(cfg.error);
    }
    return cart;
  }
  async function purchase(form, items, engine, source) {
    const cfg = config();
    if (!cfg?.enabled || !cfg.eligible.includes(Number(form.productId))) return engine.addToCart(items, { source });
    if (locked) return { ok: false, reason: 'busy', message: cfg.working };
    locked = true;
    let tx;
    try {
      if (!cfg.giftId || !cfg.variants?.length) throw new Error(cfg.error);
      let cart = await purchaseCart(engine, cfg);
      // An already selected/purchased Donut Ring can fulfil the native Buy X Get Y rule.
      // Do not automatically add another gift when one Donut Ring is already in the bag.
      let hasDonut = cart.items.some(i => i.quantity > 0 && Number(i.product_id) === Number(cfg.giftId));
      let gift = null;
      if (!hasDonut) {
        gift = await choose(cfg, document.activeElement || form.submitBtn);
        if (!gift) return { ok: false, reason: 'cancelled' };
      }
      // Re-read after the modal, since another browser tab may have changed the bag.
      cart = await purchaseCart(engine, cfg);
      hasDonut = cart.items.some(i => i.quantity > 0 && Number(i.product_id) === Number(cfg.giftId));
      if (!hasDonut && !gift) {
        gift = await choose(cfg, form.submitBtn);
        if (!gift) return { ok: false, reason: 'cancelled' };
      }
      tx = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const payload = items.map(item => ({ ...item, properties: { ...item.properties, _ondo_ring_tx: tx } }));
      if (!hasDonut && gift) payload.push({ id: gift.id, quantity: 1, properties: { [MARK]: 'v1', _ondo_ring_tx: tx } });
      const result = await engine.addToCart(payload, { source });
      if (!result.ok) {
        // Shopify can partially add a multi-item request: remove only lines from this transaction.
        await rollback(tx, engine);
        return { ...result, message: result.message || cfg.error };
      }
      return result;
    } catch (error) {
      return { ok: false, message: error?.message || cfg.error };
    } finally { locked = false; }
  }
  // Check persisted gift lines on load and after writers outside the shared engine.
  // The engine handles normal quantity/remove updates synchronously within its own queue.
  async function reconcile() {
    const cfg = config();
    if (cleanup || locked || !cfg?.giftId) return;
    cleanup = true;
    try {
      const engine = await import(cfg.cartJs);
      if (engine.isBusy()) return;
      const cart = await readCart();
      const gifts = cart.items.filter(i => i.properties?.[MARK] === 'v1' && i.quantity > 0);
      if (!gifts.length) return;
      const qualifies = cfg.enabled && cart.items.some(i => cfg.eligible.includes(Number(i.product_id)) && i.quantity > 0 && i.properties?.[MARK] !== 'v1');
      if (!qualifies || gifts.length > 1 || gifts.some(i => i.quantity !== 1 || i.final_line_price !== 0)) {
        const result = await engine.refresh();
        if (!result.ok) notice(cfg.error);
        else if (!qualifies) notice(cfg.removed);
      }
    } catch (_) { notice(cfgError()); }
    finally { cleanup = false; }
  }
  // Recheck marked gifts before normal cart checkout links/buttons submit.
  // Never allow a failed cleanup to silently turn the selected gift into a paid item.
  const approved = new WeakSet();
  let checkingCheckout = false;
  function invalidGift(cart, cfg) {
    const gifts = cart.items.filter(i => i.quantity > 0 && i.properties?.[MARK] === 'v1');
    if (!gifts.length) return false;
    const qualifies = cfg.enabled && cart.items.some(i => i.quantity > 0 && cfg.eligible.includes(Number(i.product_id)) && i.properties?.[MARK] !== 'v1');
    return !qualifies || gifts.length !== 1 || gifts.some(i => Number(i.product_id) !== Number(cfg.giftId) || i.quantity !== 1 || i.final_line_price !== 0);
  }
  async function guardCheckout(event) {
    const cfg = config();
    if (!cfg?.giftId) return;
    let target;
    if (event.type === 'click') {
      target = event.target instanceof Element ? event.target.closest('button[name="checkout"], input[name="checkout"], a[href]') : null;
      if (target?.tagName === 'A') {
        const url = new URL(target.href, location.href);
        if (!/\/checkout\/?$/.test(url.pathname)) return;
      }
    } else {
      target = event.target;
      if (!(target instanceof HTMLFormElement) || event.submitter?.name !== 'checkout') return;
    }
    if (!target) return;
    if (approved.has(target)) { approved.delete(target); return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingCheckout || locked) { notice(cfg.working); return; }
    checkingCheckout = true;
    try {
      const engine = await import(cfg.cartJs);
      if (engine.isBusy()) { notice(cfg.working); return; }
      let cart = await readCart();
      if (invalidGift(cart, cfg)) {
        const result = await engine.refresh();
        cart = await readCart();
        if (!result.ok || invalidGift(cart, cfg)) { notice(cfg.error); return; }
        notice(cfg.discountError);
        return; // Let the customer review the corrected bag before checking out again.
      }
      approved.add(target);
      if (event.type === 'submit') target.requestSubmit(event.submitter);
      else {
        // A native button click also emits submit; approve that associated form once.
        if (target.form) approved.add(target.form);
        target.click();
        if (target.form) approved.delete(target.form);
      }
    } catch (_) { notice(cfg.error); }
    finally { checkingCheckout = false; }
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  window.OndoRingGift = { mount, purchase };
  document.addEventListener('ondo:ring-gift:warning', event => notice(event.detail?.message));
  document.addEventListener('ondo:cart:updated', event => {
    if (event.detail?.action === 'external') setTimeout(reconcile, 50);
  });
  document.addEventListener('shopify:section:load', () => { mount(); reconcile(); });
  document.addEventListener('shopify:section:unload', () => { if (pending) pending(null); });
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; mount(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  // Upgrade timing differs between cached desktop pages and a fresh session.
  if (window.customElements) {
    window.customElements.whenDefined('ondo-product-form').then(() => mount());
  }
  window.addEventListener('pageshow', () => { mount(); reconcile(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') { mount(); reconcile(); }
  });
  mount();
  reconcile();
})();
